import { Field, Form, Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { array, lazy, object, string } from 'yup';

import {
  FetchError,
  useBulkCreateProfitItems,
  useBulkUpdateProfitItems,
  UseBulkCreateProfitItemsData,
  UseBulkEditProfitItemsData,
  useQuoteProfitItems,
} from '@frontend/api';
import type {
  BulkUpdateProfitItemError,
  ComponentData,
  ProfitItemData,
} from '@frontend/api';
import {
  Button,
  Center,
  DrawerBody,
  DrawerButtonGroup,
  DrawerFooter,
  FormControl,
  FormLabel,
  OutlineCircleInfoIcon,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Tooltip,
  Box,
  Popconfirm,
} from '@frontend/components';

import { receiveComponents } from 'modules/quotes/quoteItems/data/actions';

import { getQuoteId } from 'store/quotes/selectors';

import { notifyError, notifySuccess } from 'utils/notifyUtils';
import { pluralize } from 'utils/stringUtils';

import { quoteItemSelect } from '../data/actions';
import { ProfitItemsTable } from './ProfitItemsTable';
import { SharedProfitItemsTable } from './SharedProfitItemsTable';
import * as actions from './data/actions';
import { getComponents } from './data/reducer';
import type { FormValues } from './types';

const messages = {
  ACTION: 'To update pricing items, select an action',
  PROFIT_ITEMS:
    'To add pricing items, first select one or more pricing items to add',
};

const formSchema = lazy((values: FormValues) =>
  object().shape({
    action: string().required(messages.ACTION),
    profitItems:
      values.action === 'add'
        ? array().required().min(1, messages.PROFIT_ITEMS)
        : array().required(messages.PROFIT_ITEMS),
  }),
);

const extractNestedErrorMessages = (obj: unknown): string | undefined => {
  if (typeof obj === 'string') return obj;
  if (Array.isArray(obj)) {
    const found = obj.map(extractNestedErrorMessages).filter(Boolean);
    return found.length ? found.join('; ') : undefined;
  }
  if (obj && typeof obj === 'object') {
    const found = Object.values(obj)
      .map(extractNestedErrorMessages)
      .filter(Boolean);
    return found.length ? found.join('; ') : undefined;
  }
  return undefined;
};

const getStructuredErrors = (
  error: unknown,
): BulkUpdateProfitItemError[] | undefined => {
  if (!(error instanceof FetchError)) return undefined;

  const errors = (
    error.extra as {
      profitItems?: { errors?: BulkUpdateProfitItemError[] };
    }
  )?.profitItems?.errors;

  return Array.isArray(errors) && errors.length > 0 ? errors : undefined;
};

const getErrorDescription = (error: unknown): string | undefined => {
  const structured = getStructuredErrors(error);
  if (structured) {
    return [...new Set(structured.map((e) => e.error))].join('; ');
  }

  const message = (error as Error)?.message;
  if (message && message !== 'Unknown error.') return message;

  if (error instanceof FetchError && error.originalResponse) {
    return extractNestedErrorMessages(error.originalResponse);
  }

  return undefined;
};

const getInitialValues = (
  quoteProfitItemsData: ProfitItemData[],
  componentIds: Set<number>,
): FormValues => {
  const profitItemDefToComponentIds = new Map();
  quoteProfitItemsData.forEach((profitItem) => {
    const compIds =
      profitItemDefToComponentIds.get(profitItem.profitItemDefId) || new Set();
    compIds.add(profitItem.componentId);
    profitItemDefToComponentIds.set(profitItem.profitItemDefId, compIds);
  });

  const sharedProfitItemDefs = new Set();
  profitItemDefToComponentIds.forEach((compIds, profitItemDef) => {
    const areSetsEqual =
      compIds.size === componentIds.size &&
      [...compIds].every((compId) => componentIds.has(compId));
    if (areSetsEqual) {
      sharedProfitItemDefs.add(profitItemDef);
    }
  });

  const dedupedProfitItems = new Map();
  quoteProfitItemsData.forEach((profitItem) => {
    dedupedProfitItems.set(profitItem.profitItemDefId, profitItem);
  });

  const sharedProfitItems: ProfitItemData[] = [];
  sharedProfitItemDefs.forEach((sharedProfitItemDef) => {
    const profitItem = dedupedProfitItems.get(sharedProfitItemDef);
    sharedProfitItems.push(profitItem);
  });

  return {
    action: 'add',
    updateType: 'actual',
    applyOverrides: false,
    profitItems: [],
    sharedProfitItems: sharedProfitItems,
  };
};

interface ReplacePopconfirmProps {
  itemCount: number;
  onConfirm: () => void;
  children: React.ReactNode;
}

const ReplacePopconfirm = ({
  itemCount,
  onConfirm,
  children,
}: ReplacePopconfirmProps) => {
  return (
    <Popconfirm
      title={`Replace pricing items on ${pluralize('item', itemCount)}?`}
      label={
        <>
          <Text>Existing pricing items will be removed.</Text>
          <Text>This can&apos;t be undone.</Text>
        </>
      }
      confirmProps={{
        colorScheme: 'destructive',
        children: 'Replace pricing items',
      }}
      onConfirm={onConfirm}
    >
      {children}
    </Popconfirm>
  );
};

export const BulkEditProfitItemsDrawerForm = () => {
  const quoteId = useSelector(getQuoteId);
  const components: ComponentData[] = useSelector(getComponents);

  const dispatch = useDispatch();
  const onClose = useCallback(() => dispatch(actions.close()), [dispatch]);

  const [serverErrors, setServerErrors] = useState<Map<number, string>>(
    () => new Map(),
  );

  const clearServerError = useCallback((profitItemDefId: number) => {
    setServerErrors((prev) => {
      if (!prev.has(profitItemDefId)) return prev;
      const next = new Map(prev);
      next.delete(profitItemDefId);
      return next;
    });
  }, []);

  const clearAllServerErrors = useCallback(() => {
    setServerErrors((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  const handleServerErrors = useCallback((error: unknown) => {
    const errors = getStructuredErrors(error);
    if (!errors) return;

    setServerErrors(
      new Map(errors.map((e) => [Number(e.profitItemDefId), e.error])),
    );
  }, []);

  const { mutate: bulkCreateProfitItems, isLoading: isBulkCreateLoading } =
    useBulkCreateProfitItems({
      queryOptions: {
        onSuccess: (response, requestData) => {
          const mappedResponse = response.reduce<
            Record<string, UseBulkCreateProfitItemsData[number]>
          >((ax, dx) => ({ ...ax, [dx.id]: dx }), {});

          dispatch(
            receiveComponents(
              components.map((component) => ({
                ...component,
                ...mappedResponse[component.id],
              })),
            ),
          );
          dispatch(quoteItemSelect.deselectAll());
          notifySuccess(
            requestData.deleteExistingProfitItems
              ? 'Replaced pricing items'
              : `Added ${pluralize(
                  'pricing item',
                  requestData.profitItems.length,
                )}`,
          );
          onClose();
        },
        onError: (error, requestData) => {
          handleServerErrors(error);
          notifyError(
            `Failed to ${
              requestData.deleteExistingProfitItems
                ? 'replace pricing items'
                : `add ${pluralize(
                    'pricing item',
                    requestData.profitItems.length,
                  )}`
            }`,
            getErrorDescription(error),
          );
        },
      },
    });

  const { mutate: bulkUpdateProfitItems, isLoading: isBulkUpdateLoading } =
    useBulkUpdateProfitItems({
      queryOptions: {
        onSuccess: (response, requestData) => {
          const mappedResponse = response.reduce<
            Record<string, UseBulkEditProfitItemsData[number]>
          >((ax, dx) => ({ ...ax, [dx.id]: dx }), {});

          dispatch(
            receiveComponents(
              components.map((component) => ({
                ...component,
                ...mappedResponse[component.id],
              })),
            ),
          );
          setServerErrors(new Map());
          dispatch(quoteItemSelect.deselectAll());
          notifySuccess(
            `Updated ${pluralize(
              'pricing item',
              requestData.profitItems.length,
            )}`,
          );
          onClose();
        },
        onError: (error, requestData) => {
          handleServerErrors(error);
          notifyError(
            `Failed to update ${pluralize(
              'pricing item',
              requestData.profitItems.length,
            )}`,
            getErrorDescription(error),
          );
        },
      },
    });

  const {
    data: quoteProfitItemsData,
    isLoading: isQuoteProfitItemsLoading,
    isFetchedAfterMount: isQuoteProfitItemsFetchedAfterMount,
  } = useQuoteProfitItems(
    {
      componentIds: components.map(({ id }) => id),
      quoteId,
    },
    { queryOptions: { enabled: !!components.length } },
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      setServerErrors(new Map());
      if (values.action === 'update') {
        bulkUpdateProfitItems({
          quoteId,
          componentIds: components.map(({ id }) => id),
          updateType: values.updateType,
          applyOverrides: values.applyOverrides,
          profitItems: values.sharedProfitItems
            .filter((sharedProfitItem) => !!sharedProfitItem.manualPercentage)
            .map((sharedProfitItem) => ({
              profitItemDefId: sharedProfitItem.profitItemDefId,
              manualPercentage: sharedProfitItem.manualPercentage,
            })),
        });
      } else {
        bulkCreateProfitItems({
          quoteId,
          componentIds: components.map(({ id }) => id),
          deleteExistingProfitItems: values.action === 'replace',
          profitItems: values.profitItems.map((profitItem) => ({
            profitItemDefId: profitItem.profitItemDefId,
            ...(profitItem.manualPercentage !== undefined &&
              profitItem.manualPercentage !== null && {
                manualPercentage: profitItem.manualPercentage,
              }),
          })),
        });
      }
    },
    [bulkCreateProfitItems, bulkUpdateProfitItems, quoteId, components],
  );

  const initialValues = useMemo(
    () =>
      getInitialValues(
        quoteProfitItemsData ?? [],
        new Set(components.map(({ id }) => id)),
      ),
    [quoteProfitItemsData, components],
  );

  if (isQuoteProfitItemsLoading || !isQuoteProfitItemsFetchedAfterMount) {
    return (
      <>
        <Center flex={1}>
          <Spinner size="lg" />
        </Center>
      </>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={onSubmit}
    >
      {({ setFieldValue, errors, dirty, isValid, values }) => {
        const buttonTooltipLabel = !dirty
          ? messages.PROFIT_ITEMS
          : ((errors.action ?? errors.profitItems) as string | undefined);

        const commonButtonProps = {
          children: 'Update Pricing Items',
          isLoading:
            values.action === 'update'
              ? isBulkUpdateLoading
              : isBulkCreateLoading,
          isDisabled: !dirty || !isValid,
        };

        return (
          <>
            <DrawerBody
              as={Form}
              id="bulk-edit-profitItems-form"
              noValidate
              autoComplete="off"
              display="flex"
              flexDir="column"
              gap={3}
            >
              <FormControl isRequired>
                <FormLabel>Action</FormLabel>
                <Field
                  name="action"
                  as={RadioGroup}
                  onChange={(v: string) => {
                    setFieldValue('action', v);
                    clearAllServerErrors();
                  }}
                >
                  <Radio value="add">Add pricing items</Radio>
                  <Radio value="replace">Replace all pricing items</Radio>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={0.5}
                    alignItems="center"
                  >
                    <Tooltip
                      label="There are no shared pricing items"
                      shouldWrapChildren={!values.sharedProfitItems?.length}
                      isDisabled={!!values.sharedProfitItems?.length}
                    >
                      <Radio
                        value="update"
                        isDisabled={!values.sharedProfitItems?.length}
                      >
                        {`Update shared pricing items (${values.sharedProfitItems?.length})`}
                      </Radio>
                    </Tooltip>
                    <Tooltip
                      placement="bottom"
                      label="Update the pricing items that appear on each of the selected line items"
                    >
                      <OutlineCircleInfoIcon
                        boxSize="1.25rem"
                        color="feature.secondary"
                      />
                    </Tooltip>
                  </Box>
                </Field>
              </FormControl>
              {values.action === 'update' ? (
                <SharedProfitItemsTable
                  serverErrors={serverErrors}
                  onClearServerError={clearServerError}
                  onClearAllServerErrors={clearAllServerErrors}
                />
              ) : (
                <ProfitItemsTable
                  serverErrors={serverErrors}
                  onClearServerError={clearServerError}
                />
              )}
            </DrawerBody>
            <DrawerFooter>
              <DrawerButtonGroup>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Tooltip label={buttonTooltipLabel}>
                  {values.action === 'add' || values.action === 'update' ? (
                    <Button
                      form="bulk-edit-profitItems-form"
                      type="submit"
                      {...commonButtonProps}
                    />
                  ) : (
                    <ReplacePopconfirm
                      itemCount={components.length}
                      onConfirm={() => onSubmit(values)}
                    >
                      <Button {...commonButtonProps} />
                    </ReplacePopconfirm>
                  )}
                </Tooltip>
              </DrawerButtonGroup>
            </DrawerFooter>
          </>
        );
      }}
    </Formik>
  );
};
